import type { FC } from "react";
import { Suspense, lazy } from "react";
import type { DehydratedState, QueryClient } from "@tanstack/react-query";
import { HydrationBoundary, QueryClientProvider } from "@tanstack/react-query";
import type { i18n as I18nInstance } from "i18next";
import { I18nextProvider } from "react-i18next";
import type { PartialDeep } from "type-fest";
import { ClassicForm as ClassicFormWidget } from "@/widgets/classic-form";
import { CollapsibleViewProvider } from "@/features/collapsible-view";
import type { SearchDataModel } from "@/entities/data-model";
import { DataModelProvider } from "@/entities/data-model";
import type { FormMetadataParams } from "@/entities/form-metadata";
import { MetadataParamsProvider } from "@/entities/form-metadata";
import { ErrorBoundary } from "@/shared/components/ErrorBoundary";
import { useEffectOnce } from "@/shared/hooks/useEffectOnce";
import { FCIDetailsProvider } from "@/shared/lib/fci";
import type { SubmitHandler } from "@/shared/submit-handler";
import { SubmitHandlerProvider } from "@/shared/submit-handler";
import { usePrefetchQueries } from "@/pages/classic-form/usePrefetchQueries";

const LazyDebugPanel = lazy(() => import("@/features/debug-panel/DebugPanel").then((m) => ({ default: m.DebugPanel })));

export interface ClassicFormProps {
    formMetadataParams: FormMetadataParams;
    dataModel?: PartialDeep<SearchDataModel>;
    queryClient: QueryClient;
    i18n: I18nInstance;
    dehydratedState?: DehydratedState;
    isEditForm?: boolean;
    /** Callback fired when the component is mounted and ready */
    onReady?: () => void;
    /** Custom submit handler. If provided, called instead of window.open("/search/?...") */
    onSubmit?: SubmitHandler<SearchDataModel>;
}

export const ClassicForm: FC<ClassicFormProps> = ({
    formMetadataParams,
    dataModel,
    queryClient,
    i18n: customI18n,
    dehydratedState,
    isEditForm = false,
    onReady,
    onSubmit,
}) => {
    useEffectOnce(() => {
        onReady?.();
    });

    usePrefetchQueries(queryClient, {
        formMetadataParams,
    });

    return (
        <QueryClientProvider client={queryClient}>
            <HydrationBoundary state={dehydratedState}>
                <FCIDetailsProvider details={formMetadataParams}>
                    <I18nextProvider i18n={customI18n}>
                        <MetadataParamsProvider value={formMetadataParams}>
                            <DataModelProvider value={dataModel}>
                                {formMetadataParams.debug && (
                                    <ErrorBoundary>
                                        <Suspense fallback={null}>
                                            <LazyDebugPanel />
                                        </Suspense>
                                    </ErrorBoundary>
                                )}
                                <CollapsibleViewProvider>
                                    <SubmitHandlerProvider onSubmit={onSubmit}>
                                        <ClassicFormWidget isEditForm={isEditForm} />
                                    </SubmitHandlerProvider>
                                </CollapsibleViewProvider>
                            </DataModelProvider>
                        </MetadataParamsProvider>
                    </I18nextProvider>
                </FCIDetailsProvider>
            </HydrationBoundary>
        </QueryClientProvider>
    );
};
